import React from 'react'; // BusinessCard with outlined icons updated 
import { socialUrl, SocialNetwork } from '../../utils/socialLinks';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
} from '@mui/material';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Business } from '../../types/business';
import { API_ORIGIN } from '../../config/api';
import { ownershipOf } from './OwnershipBadge';
import { isIranBusiness, IRAN_COLOR, IRAN_TINT, IRAN_TAG_LABEL } from '../../utils/iranBusiness';
import { FEATURED_LABEL, FEATURED_COLOR, FEATURED_OUTLINE, PREMIUM_LABEL, PREMIUM_COLOR, PREMIUM_OUTLINE } from '../../utils/featured';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VerifiedIcon from '@mui/icons-material/Verified';

// Hide the view counter on cards until a listing reaches this many views.
const MIN_VISIBLE_VIEWS = 10;

interface BusinessCardProps {
    business: Business;
    /**
     * Used by the "how will this look in search results" preview on the
     * registration/edit form: the previewed business isn't saved yet (no
     * real id/slug), so the normal click-to-navigate would send the user to
     * a business page that doesn't exist. Setting this renders the card
     * inert (no navigation, no social-link opens) instead.
     */
    disableNavigation?: boolean;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ business, disableNavigation = false }) => {
    const navigate = useNavigate();

    const getImageUrl = (path: string | null | undefined) => {
        if (!path) return null;
        // blob:/data: = photos picked in the registration form but not uploaded
        // yet (the listing preview). Prefixing the API origin broke them.
        if (/^(https?:|blob:|data:)/i.test(path)) return path;
        return `${API_ORIGIN}${path}`;
    };

    // Many migrated listings have no featured/cover photo (mainImage) but do have
    // a company logo - previously the card fell straight through to a generic
    // stock photo of someone pointing at a laptop, which read as broken/wrong for
    // every one of those listings. Prefer the logo before giving up to the stock photo.
    const cardImageUrl = getImageUrl(business.mainImage) || getImageUrl(business.logoUrl) ||
        'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=600&h=400&q=80';

    const handleSocialClick = (e: React.MouseEvent, network: SocialNetwork, raw: string) => {
        e.stopPropagation();
        if (disableNavigation) return;
        const url = socialUrl(network, raw);
        if (url) window.open(url, '_blank', 'noopener');
    };

    const inIran = isIranBusiness(business);
    const premium = Boolean(business.isPremium);
    const featured = premium || Boolean(business.isFeatured);

    return (
        <Card
            onClick={() => { if (!disableNavigation) navigate(`/business/${business.slug || business.id}`); }}
            sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: disableNavigation ? 'default' : 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: 2,
                overflow: 'visible', // allow avatar to overflow
                border: inIran ? `2px solid ${IRAN_COLOR}` : premium ? `2px solid ${PREMIUM_OUTLINE}` : featured ? `2px solid ${FEATURED_OUTLINE}` : '1px solid #e5e7eb',
                bgcolor: inIran ? IRAN_TINT : undefined,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)'
                }
            }}
        >
            {/* Image Section with Overlays */}
            <Box sx={{ position: 'relative', height: 220, width: '100%', borderTopLeftRadius: 8, borderTopRightRadius: 8, overflow: 'hidden', bgcolor: '#111827' }}>
                {/* Blurred, zoomed copy of the same photo fills the card edge-to-edge so
                    switching the real photo to object-fit: contain (below) never leaves
                    plain empty bars - cover-cropping was cutting heads/edges off photos
                    whose aspect ratio didn't match this fixed 220px-tall card. */}
                <Box
                    component="img"
                    src={cardImageUrl}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'blur(20px) brightness(0.55)',
                        transform: 'scale(1.15)', // hide the blur's soft edge
                    }}
                />
                <CardMedia
                    component="img"
                    height="100%"
                    image={cardImageUrl}
                    alt={business.name}
                    loading="lazy"
                    decoding="async"
                    sx={{
                        position: 'relative',
                        width: '100%',
                        // contain shows the entire photo with nothing cropped off -
                        // any leftover space above/below or left/right is filled by
                        // the blurred layer behind it instead of being left blank.
                        objectFit: 'contain',
                        transition: 'transform 0.5s ease',
                        '&:hover': { transform: 'scale(1.05)' }
                    }}
                />

                {/* Top Left: featured (paid) and/or based in Iran (not an Australian business) */}
                {(featured || inIran) && (
                    <Box dir="rtl" sx={{ position: 'absolute', top: 12, left: 12, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                        {featured && (
                            <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                bgcolor: premium ? PREMIUM_COLOR : FEATURED_COLOR, color: '#fff', borderRadius: '6px',
                                px: 1.1, py: 0.35, fontSize: '0.72rem', fontWeight: 700,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                            }}>
                                {premium ? <WorkspacePremiumIcon sx={{ fontSize: 14 }} /> : <RocketLaunchIcon sx={{ fontSize: 14 }} />}
                                {premium ? PREMIUM_LABEL : FEATURED_LABEL}
                            </Box>
                        )}
                        {inIran && (
                            <Box sx={{
                                bgcolor: IRAN_COLOR, color: '#fff', borderRadius: '6px',
                                px: 1.2, py: 0.4, fontSize: '0.72rem', fontWeight: 700,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            }}>
                                {IRAN_TAG_LABEL}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Top Right Category Overlay */}
                {business.category?.name && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(4px)',
                            borderRadius: '6px',
                            px: 1.2,
                            py: 0.4,
                            color: '#1e293b',
                            zIndex: 2,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        {business.category.name}
                    </Box>
                )}


                {/* Bottom Overlay (Gradient & Info Stack) */}
                <Box
                    dir="ltr"
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent 20%, rgba(0,0,0,0.85) 100%)',
                        p: 2,
                        pt: 6,
                        color: 'white',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        textAlign: 'left'
                    }}
                >

                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: '700',
                            lineHeight: 1.2,
                            mb: 0.5,
                            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 1
                        }}
                    >
                        {/* A real <a href> so search engines can follow it to the
                            listing (the card's own click handler is invisible to
                            crawlers) and visitors can open it in a new tab. */}
                        {disableNavigation ? business.name : (
                            <Box
                                component={RouterLink}
                                to={`/business/${business.slug || business.id}`}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{ color: 'inherit', textDecoration: 'none !important' }}
                            >
                                {business.name}
                            </Box>
                        )}
                        {/* Claimed by the owner: a blue tick after the name (cards show nothing for auto listings). */}
                        {ownershipOf(business) === 'claimed' && (
                            <Tooltip title="تأییدشده توسط مالک">
                                <VerifiedIcon sx={{ fontSize: '1.05em', color: '#1d9bf0', ml: 0.5, verticalAlign: '-0.15em', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} />
                            </Tooltip>
                        )}
                    </Typography>

                    {/* Short Persian description of the business: under the name, smaller. */}
                    {business.persianTitle && (
                        <Typography
                            dir="rtl"
                            sx={{
                                fontWeight: 500,
                                mb: 0.5,
                                fontSize: '0.9rem',
                                lineHeight: 1.5,
                                opacity: 0.95,
                                textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                                display: '-webkit-box',
                                overflow: 'hidden',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 1
                            }}
                        >
                            {business.persianTitle}
                        </Typography>
                    )}

                    <Typography 
                        variant="caption" 
                        sx={{ 
                            mb: business.phone ? 0.5 : 0,
                            opacity: 0.9,
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            overflow: 'hidden',
                            '& span': {
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 1,
                                overflow: 'hidden'
                            }
                        }}
                    >
                        <FmdGoodOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                        <span>
                            {[business.address, business.city?.name].filter(val => val && val !== 'Address TBD').join(', ') || 'Address TBD'}
                        </span>
                    </Typography>

                    {business.phone && (
                        <Typography
                            variant="caption"
                            sx={{
                                opacity: 0.9,
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                            {business.phone}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Business Logo Badge - like the original Cheesta site's cards, a
                small circular logo overlapping the boundary between the photo
                and the white content area, bottom-right. Sits as a direct
                child of the Card (not the image Box, which clips its own
                blur/contain layers) so it can straddle both sections - the
                Card's overflow is already 'visible' for exactly this. The
                social icons row below is shifted left (see maxWidth/mr there)
                to leave room instead of sitting underneath it. */}
            {business.logoUrl && (
                <Box
                    component="img"
                    src={getImageUrl(business.logoUrl) || undefined}
                    alt={`${business.name} logo`}
                    loading="lazy"
                    decoding="async"
                    // A logo file that no longer exists would otherwise show the
                    // browser's broken-image icon inside the round badge.
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                    sx={{
                        position: 'absolute',
                        top: 220 - 22,
                        right: 16,
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        border: '3px solid white',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                        bgcolor: 'white',
                        // Whole logo inside the circle (wide wordmarks were cropped by "cover").
                        objectFit: 'contain',
                        p: '6px',
                        boxSizing: 'border-box',
                        zIndex: 3,
                    }}
                />
            )}

            {/* Bottom White Area */}
            <CardContent
                dir="ltr"
                sx={{
                    px: 1.5,
                    py: 1.25,
                    '&:last-child': { pb: 1.25 },
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    minWidth: 0,
                    bgcolor: 'white',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    overflow: 'hidden'
                }}
            >
                
                {/* Left Side: Views & Google Rating */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 1 }}>
                    {/* Low view counts look like an empty listing, so the
                        counter only appears once a listing has MIN_VISIBLE_VIEWS. */}
                    {(business.views || 0) >= MIN_VISIBLE_VIEWS && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5, 
                            color: '#475569' 
                        }}>
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                {(business.views || 0) >= 1000 ? `${((business.views || 0) / 1000).toFixed(1)}K` : business.views || 0} views
                            </Typography>
                        </Box>
                    )}
                    
                    {business.googlePlaceId && business.googleRating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...((business.views || 0) >= MIN_VISIBLE_VIEWS ? { borderLeft: '1px solid #e2e8f0', pl: 1 } : {}), color: '#64748b' }}>
                            <Box component="img" src="https://www.gstatic.com/images/branding/product/1x/googleg_32dp.png" sx={{ height: 14, width: 14 }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                {business.googleRating.toFixed(1)}
                            </Typography>
                        </Box>
                    )}
                </Box>
                
                {/* Right Side: Social Icons - shifted left with mr so the row
                    doesn't sit directly under the logo badge, which overlaps
                    this corner from the photo above. The badge (56px + 3px border
                    each side = 62px) spans 16-78px from the card's right edge;
                    minus CardContent's 12px padding plus an 8px gap = 74px. */}
                <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '46%', flexShrink: 0, mr: business.logoUrl ? '74px' : 0 }}>
                    {socialUrl('instagram', business.instagram) && (
                        <IconButton size="small" onClick={(e) => handleSocialClick(e, 'instagram', business.instagram!)} sx={{ color: '#E4405F', p: 0.35, '&:hover': { bgcolor: '#fdf2f2' } }}>
                            <InstagramIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    )}
                    {socialUrl('facebook', business.facebook) && (
                        <IconButton size="small" onClick={(e) => handleSocialClick(e, 'facebook', business.facebook!)} sx={{ color: '#1877F2', p: 0.35, '&:hover': { bgcolor: '#f0f7ff' } }}>
                            <FacebookIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    )}
                    {socialUrl('telegram', business.telegram) && (
                        <IconButton size="small" onClick={(e) => handleSocialClick(e, 'telegram', business.telegram!)} sx={{ color: '#0088cc', p: 0.35, '&:hover': { bgcolor: '#f0f9ff' } }}>
                            <TelegramIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    )}
                    {socialUrl('whatsapp', business.whatsapp) && (
                        <IconButton size="small" onClick={(e) => handleSocialClick(e, 'whatsapp', business.whatsapp!)} sx={{ color: '#25D366', p: 0.35, '&:hover': { bgcolor: '#f0fff4' } }}>
                            <WhatsAppIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default BusinessCard;
